/**
 * Sales Consultant dashboard.
 *
 * Dealer-lens questions this page must answer:
 *   - What do I need to act on this lead?         → My Day rows, next-step chips
 *   - How fast can I see what happens next?       → it is the first thing on screen
 *   - Can I finish without unnecessary complexity? → complete + book + next in one modal
 *
 * Nothing here that the consultant cannot personally act on: no dollar
 * values, no team data, no dealership-wide counts. That is the dealer lens
 * applied literally.
 */

import KpiTile from '../../components/dashboard/KpiTile';
import { splitSub } from '../../components/dashboard/NewUsedSplit';
import MyDay from '../MyDay';
import FunnelChart from '../../components/dashboard/FunnelChart';
import { buildConsultantRows } from '../../lib/funnelStages';
import { DonutChart, ColumnChart } from '../../components/dashboard/Pictorial';

export default function SalesConsultantDashboard({ snapshot, periodLabel }) {
  const m = snapshot?.mine;
  const funnelRows = buildConsultantRows(snapshot?.window?.funnel);

  return (
    <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── ROW 1 ── */}
      <div className="grid-4">
        <KpiTile index={0} label="MY TASKS" periodLabel="Today" to="/tasks"
          value={m?.tasksDueToday ?? '—'}
          color={(m?.tasksOverdue ?? 0) > 0 ? 'var(--danger)' : 'var(--accent-primary)'}
          tone={(m?.tasksOverdue ?? 0) > 0 ? 'danger' : undefined}
          sub={m ? `${m.tasksOverdue ?? 0} overdue` : undefined} />
        <KpiTile index={1} label="MY APPOINTMENTS" periodLabel="Today" to="/appointments"
          value={m?.appointmentsToday ?? '—'} />
        <KpiTile index={2} label="MY NEW LEADS" periodLabel="Today" to="/leads"
          value={m?.newLeadsToday ?? '—'}
          sub={m?.newLeadsSplit ? splitSub(m.newLeadsSplit) : undefined} />
        <KpiTile index={3} label="NO NEXT STEP" periodLabel="Now" to="/leads?filter=no-next-step"
          value={m?.leadsWithoutNextEvent ?? '—'}
          color={(m?.leadsWithoutNextEvent ?? 0) > 0 ? 'var(--warning)' : 'var(--success)'}
          tone="warning" sub="set one for each" />
      </div>

      {/* ── ROW 1b: today at a glance, pictorial ── */}
      {m && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
          <div className="card">
            <div className="card-label" style={{ marginBottom: 8 }}>MY TASKS · TODAY</div>
            <DonutChart centerLabel="tasks" segments={[
              { label: 'Due today', value: m.tasksDueToday, color: 'var(--accent-primary)' },
              { label: 'Overdue', value: m.tasksOverdue, color: 'var(--danger)' },
            ]} />
          </div>
          <div className="card">
            <div className="card-label" style={{ marginBottom: 8 }}>MY DAY AT A GLANCE</div>
            <ColumnChart height={130} bars={[
              { label: 'Appointments', value: m.appointmentsToday, color: 'var(--accent-primary)' },
              { label: 'New leads',    value: m.newLeadsToday,     color: 'var(--info)' },
              { label: 'No next step', value: m.leadsWithoutNextEvent, color: 'var(--warning)' },
            ]} />
          </div>
        </div>
      )}

      {/* ── ROW 2: My Day, in full — the actual "who do I call today" list ── */}
      <MyDay embedded />

      {/* ── ROW 3: my pipeline, volume only (F1) — early stages combined, MyDay
           above is where individual leads and next actions live ── */}
      <div className="card">
        <div className="card-label" style={{ marginBottom: 8 }}>MY PIPELINE{periodLabel ? ` · ${periodLabel.toUpperCase()}` : ''}</div>
        <FunnelChart rows={funnelRows} periodLabel={periodLabel} compact caption="My pipeline by stage" />
      </div>
    </div>
  );
}
